// src/hooks/useConfiguracaoLogin.ts
// Busca a configuração de login do tenant (GET público /v1/configuracao-login).
// Reusado pela tela de login (renderização condicional) — e, indiretamente, pelo
// mesmo contrato que o painel admin grava. Fonte única da leitura.
//
// Estratégia de falha (issue #28 §1): qualquer erro (rede, 429, etc.) cai no
// fallback seguro = mostrar os dois métodos. Nunca esconde um método por erro —
// o backend é quem recusa o inválido.
import { useEffect, useState } from 'react';
import { apiGet } from '@/services/api';
import { logger } from '@/utils/logger';
import { CONFIGURACAO_LOGIN_FALLBACK, type ConfiguracaoLogin } from '@/types/configuracaoLogin';

interface UseConfiguracaoLoginResult {
  config: ConfiguracaoLogin | null; // null enquanto carrega
  isLoading: boolean;
}

export function useConfiguracaoLogin(): UseConfiguracaoLoginResult {
  const [config, setConfig] = useState<ConfiguracaoLogin | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelado = false;
    const carregar = async () => {
      try {
        const res = await apiGet('/v1/configuracao-login');
        if (cancelado) return;
        const data = res.data as Partial<ConfiguracaoLogin> | undefined;
        setConfig({
          permiteSenha: data?.permiteSenha ?? CONFIGURACAO_LOGIN_FALLBACK.permiteSenha,
          permiteMicrosoft: data?.permiteMicrosoft ?? CONFIGURACAO_LOGIN_FALLBACK.permiteMicrosoft,
        });
      } catch (err) {
        if (cancelado) return;
        logger.error('Erro ao carregar configuração de login:', err);
        setConfig(CONFIGURACAO_LOGIN_FALLBACK);
      } finally {
        if (!cancelado) setIsLoading(false);
      }
    };
    carregar();
    return () => {
      cancelado = true;
    };
  }, []);

  return { config, isLoading };
}
